import { gql, useQuery } from '@apollo/client'
import { navigate } from '@reach/router'
import * as React from 'react'
import { findDOMNode } from 'react-dom'
import styled from 'styled-components'
import addDefaultSrc from '../../../../common/src/addDefaultSrc'
import Button from '../../../../common/src/components/Button'
import { InlineErrorBoundary } from '../../../../common/src/components/InlineErrorBoundary'
import { Link } from '../../../../common/src/components/Link'
import { Spacer, H1, H4, ColoredSpan, ClickableSpan, IntroText } from '../../../../common/src/components/core'
import { ColorName } from '../../../../common/src/constants'
import { handleError } from '../../../../common/src/data/errors'
import { TestIds } from '../../../../common/src/data/testids'
import { addFeedback } from '../../../../common/src/feedback'
import memoize from '../../../../common/src/memoize'
import title from '../../../../common/src/title'
import {
  TaskType,
  CreateThreadMutationVariables,
  CreateThreadMutation,
  FetchTasksPageQuery,
  JobName,
  TimeseriesName,
  FetchTaskChartsQuery,
  FetchTaskChartsQueryVariables,
  LeasingTask,
  FetchUserContextQuery,
} from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { updateTask, runAdminTaskUncaught } from '../../graphql/mutations'
import ActionLog from '../components/ActionLog'
import DailyTimeseriesChart, { DailyTimeseriesChartProps, percentChartConfig } from '../components/DailyTimeseriesChart'
import JobsHistorySection from '../components/JobsHistorySection'
import { LabelledLineChartConfig } from '../components/LabelledLineChart'
import { Body, LeasingPage, LeasingPageBody, LeasingPageDetail, ScrollPane } from '../components/containers'
import { TaskText } from '../components/task'
import ContextSidebar from '../inbox/ContextSidebar'
import { getFirmPath, getTaskPath, getThreadPath } from '../route'
import AgentsSection from './AgentsSection'
import AvailabilitySection from './AvailabilitySection'
import EndpointsSection from './EndpointsSection'
import MarketingSection from './MarketingSection'
import NewThreadSection from './NewThreadSection'
import TaskEditBoxes from './TaskEditBoxes'
import TaskListSection from './TaskListSection'

const createThreadMutation = gql`
  mutation CreateThread($addressUs: String!, $addressThem: String!) {
    createThread(addressUs: $addressUs, addressThem: $addressThem) {
      id
    }
  }
`

interface TaskDetailProps {
  user: FetchUserContextQuery['self']
  task: NonNullable<FetchTasksPageQuery['task']>
}

export default class TaskDetail extends React.PureComponent<TaskDetailProps> {
  componentDidMount() {
    const root = findDOMNode(this)!.parentNode as Element
    root.scrollTop = 0
    this.updateTitle(this.props)
  }

  UNSAFE_componentWillReceiveProps(np: TaskDetail['props']) {
    this.updateTitle(np)
  }

  render() {
    const { user, task } = this.props
    const { addressEmail, addressPhone, taskType, primaryPmKeyJson } = task

    const isLeasingProperty = taskType === 'LEASING_PROPERTY'
    const isLeasingFirm = taskType === 'LEASING_FIRM'
    const isLeasing = isLeasingFirm || isLeasingProperty

    const isApmProperty = isLeasingProperty && primaryPmKeyJson && primaryPmKeyJson.includes('"APPFOLIO"')
    const canViewAdminSections = user && user.permissions.canViewAdminPage
    const isSuper = user && user.permissions.canViewSuperTaskDetails
    const canViewUnitsAndMarketing = user && user.permissions.canViewUnitsAndMarketing

    return (
      <LeasingPage>
        <LeasingPageBody>
          <ScrollPane data-testid={TestIds.leasingPageScrollPane}>
            <div>
              <StreetViewBanner address={task.streetViewAddress} onEdit={this.onEditStreetViewBound} />
              <Spacer height={16} />
            </div>
            <Body>
              <H1>
                <TaskText
                  task={task}
                  align="right"
                  iconSize={task.taskType !== TaskType.LEASING_FIRM ? 'medium' : undefined}
                />
              </H1>
              <AddressLine task={task} canViewAdmin={canViewAdminSections} onEditAddress={this.onEditAddressBound} />
              <Spacer height={32} />
              <TaskEditBoxes leasingTask={task} user={user} onEdit={this.onEditBound} />
              <Spacer height={32} />
              {addressPhone != null && addressEmail != null && (
                <NewThreadSection
                  addressPhone={addressPhone}
                  addressEmail={addressEmail}
                  createThread={this.createThreadBound}
                />
              )}

              <InlineErrorBoundary componentName="AvailabilitySection">
                {isLeasingProperty && (
                  <AvailabilitySection
                    taskId={task.id}
                    showHistoryLink={canViewAdminSections}
                    showUnlink={canViewAdminSections}
                  />
                )}
              </InlineErrorBoundary>
              <InlineErrorBoundary componentName="AgentsSection">
                {isLeasing && <AgentsSection firmId={task.firm.id} taskId={task.id} user={user} />}
              </InlineErrorBoundary>
              <InlineErrorBoundary componentName="EndpointsSection">
                {isLeasingProperty && isSuper && <EndpointsSection taskId={task.id} />}
              </InlineErrorBoundary>
              {isApmProperty && canViewUnitsAndMarketing && <MarketingSection taskId={task.id} />}
              <InlineErrorBoundary componentName="TaskListSection">
                {isLeasingFirm && <TaskListSection user={user} firmId={task.firm.id} />}
              </InlineErrorBoundary>
              <InlineErrorBoundary componentName="ChartsSection">
                {isSuper && <ChartsSection user={user} task={task} />}
              </InlineErrorBoundary>
              <InlineErrorBoundary componentName="JobsHistorySection">
                {isLeasingProperty && primaryPmKeyJson && isSuper && this.renderJobs(primaryPmKeyJson, task.firm)}
              </InlineErrorBoundary>
              <InlineErrorBoundary componentName="ActionLogSection">
                {isSuper && <ActionLog taskId={task.id} />}
              </InlineErrorBoundary>
            </Body>
          </ScrollPane>
        </LeasingPageBody>
        <LeasingPageDetail>
          <InlineErrorBoundary componentName="ContextSidebar">
            <ContextSidebar user={user} task={task} />
          </InlineErrorBoundary>
        </LeasingPageDetail>
      </LeasingPage>
    )
  }

  private onEditAddressBound = () => {
    const { address, streetViewAddress, taskType } = this.props.task
    const lines = [
      `Street view: ${streetViewAddress || 'none'}`,
      address ? `Canonical: ${formatAddress(address)}` : 'No valid address',
      taskType !== 'LEASING_PROPERTY' &&
        'WARNING: address determines timezone. Timezone determines local time used in reports, CSV API, scheduling, etc.',
      'Enter a new address:',
    ]
    const newAddress = window.prompt(lines.filter(s => s).join('\n'))
    if (newAddress == null) {
      return
    }
    this.onEditBound('address', newAddress).catch(handleError)
  }

  private onEditStreetViewBound = () => {
    const newAddress = window.prompt('Enter a new street view address:')
    if (newAddress == null) {
      return
    }
    this.onEditBound('streetViewAddress', newAddress).catch(handleError)
  }

  private onEditBound = (key: string, value: string) => {
    const { task } = this.props
    const predictResponse = key !== 'address'
    return updateTask(task, key, value, predictResponse)
  }

  private createThreadBound = (addressUs: string, addressThem: string) => {
    const variables: CreateThreadMutationVariables = { addressUs, addressThem }
    return apolloWebClient
      .mutate<CreateThreadMutation>({
        mutation: createThreadMutation,
        variables,
      })
      .then(result => {
        addFeedback('Created thread')
        const newThread = result.data!.createThread
        return navigate(getThreadPath(newThread.id))
      })
  }

  private updateTitle(props: TaskDetail['props']) {
    title.setMaybeFlashing(props.task.displayName)
  }

  private renderJobs(
    primaryPmKeyJson: string,
    firm: LeasingTask['firm'] | NonNullable<FetchTasksPageQuery['task']>['firm']
  ) {
    const { firmName, id } = firm
    const syncButtons = [
      {
        label: `Sync all ${firmName} tasks`,
        handler: () => runAdminTaskUncaught({ name: JobName.ADMIN_RERUN_AUDITS, firmId: id }),
      },
    ]
    const isApm = primaryPmKeyJson.includes('"APPFOLIO"')
    const jobKey = isApm ? firm.pmKeyJsons[0] : primaryPmKeyJson

    return (
      <div>
        <H4>Jobs</H4>
        <Spacer height={16} />
        <JobsHistorySection jobKey={jobKey} buttons={syncButtons} compact />
      </div>
    )
  }
}

function formatAddress(address: NonNullable<NonNullable<FetchTasksPageQuery['task']>['address']>) {
  const { street, city, state, zipcode } = address.addressParts
  return `${street}, ${city}, ${state} ${zipcode}`
}

function AddressLine({
  task,
  canViewAdmin,
  onEditAddress,
}: {
  task: NonNullable<FetchTasksPageQuery['task']>
  canViewAdmin: boolean
  onEditAddress: () => void
}) {
  const { address } = task
  return (
    <AddressLineWrap>
      <AddressText address={address} onEdit={onEditAddress} />
      {task.firmTaskId && task.id !== task.firmTaskId && (
        <Link color="snow" href={getTaskPath(task.firmTaskId)}>
          {task.firm.firmName}
        </Link>
      )}
      {canViewAdmin && task.id === task.firmTaskId && (
        <Link color="snow" to={getFirmPath(task.firm.id)}>
          admin
        </Link>
      )}
    </AddressLineWrap>
  )
}

function AddressText({
  address,
  onEdit,
}: {
  address?: NonNullable<FetchTasksPageQuery['task']>['address'] | null
  onEdit: () => void
}) {
  let text
  let color: ColorName | undefined
  if (address) {
    text = formatAddress(address)
  } else {
    text = 'Invalid address'
    color = 'coral'
  }
  return (
    <ColoredSpan color={color} onDoubleClick={onEdit} onClick={address ? undefined : onEdit}>
      <ClickableSpan>{text}</ClickableSpan>
    </ColoredSpan>
  )
}

const AddressLineWrap = styled(IntroText)`
  display: flex;
  justify-content: space-between;
`

function StreetViewBanner({ address, onEdit }: { address?: string | null; onEdit: () => void }) {
  const src = 'https://dynasty-streetview.s3-us-west-2.amazonaws.com/' + address + ' LARGE'
  return (
    <StreetViewWrapper onDoubleClick={onEdit}>
      <img
        src={src}
        onError={addDefaultSrc(
          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='580' height='160'%3E%3Crect fill='%23333' width='580' height='160'/%3E%3C/svg%3E"
        )}
      />
    </StreetViewWrapper>
  )
}

const StreetViewWrapper = styled.div`
  width: 579px;
  height: 128px;
  overflow: hidden;
`

interface ChartsSectionProps {
  user: FetchUserContextQuery['self']
  task: NonNullable<FetchTasksPageQuery['task']>
}

class ChartsSection extends React.PureComponent<ChartsSectionProps, { hover?: string; select?: string }> {
  private handlers = {}
  state: { hover?: string; select?: string } = { select: 'Messages' }

  render() {
    const { user, task } = this.props
    const { hover, select } = this.state

    const showOccupancy = user.permissions.canViewOccupancy
    const isFirm = task.taskType === TaskType.LEASING_FIRM
    const modelType = isFirm ? 'FIRM' : 'TASK'

    const charts = [
      {
        name: 'Messages',
        tsName: TimeseriesName.MESSAGES,
        yLabel: 'inbound + outbound messages',
        color: 'sky',
      },
      {
        name: 'Showings',
        tsName: TimeseriesName.SHOWINGS,
        yLabel: 'total # prospects attending a showing',
        color: 'lemon',
      },
    ] as { name: string; tsName: TimeseriesName; yLabel: string; color: ColorName; config?: LabelledLineChartConfig }[]

    if (showOccupancy) {
      charts.push({
        name: 'Occupancy',
        tsName: TimeseriesName.OCCUPANCY,
        yLabel: '% occupied',
        config: percentChartConfig,
        color: 'mint',
      })
    }

    charts.push({
      name: 'Unit count',
      tsName: TimeseriesName.UNIT_COUNT,
      yLabel: '# rentable units',
      color: 'coral',
    })

    const chart = charts.find(c => c.name === hover) || charts.find(c => c.name === select) || charts[0]

    return (
      <div>
        <H4>{modelType} STATS</H4>
        {charts.length > 1 && (
          <div>
            <Spacer height={16} />
            {charts.map(({ name, color }, i) => [
              <Button
                key={i}
                color={color}
                filled={select === name}
                size="small"
                onClick={memoize(this.handlers, { name, ev: 'click' }, () => () => this.select(name))}
              >
                {name}
              </Button>,
              <Spacer key={`spacer-${i}`} width={8} />,
            ])}
          </div>
        )}
        <Spacer height={16} />
        <TimeseriesChart
          config={chart.config}
          tsName={chart.tsName}
          firmId={isFirm ? task.firm.id : undefined}
          taskId={isFirm ? undefined : task.id}
          series={[{ name: chart.yLabel, key: 'timeseries', color: chart.color }]}
        />
        <Spacer height={32} />
      </div>
    )
  }

  private select(name?: string) {
    const select = name === this.state.select ? undefined : name
    this.setState({ select })
  }
}

const fetchTaskChartsQuery = gql`
  query FetchTaskCharts($tsName: TimeseriesName!, $firmId: Int, $taskId: Int) {
    timeseries(tsName: $tsName, modelFilter: { firmId: $firmId, taskId: $taskId }) {
      date
      value
    }
  }
`

interface ModelFilterProps {
  firmId?: number
  taskId?: number
}

interface TimeseriesChartProps extends ModelFilterProps {
  tsName: TimeseriesName
}

function TimeseriesChart(props: DailyTimeseriesChartProps & TimeseriesChartProps) {
  const { tsName, firmId, taskId } = props
  const { loading, error, data } = useQuery<FetchTaskChartsQuery, FetchTaskChartsQueryVariables>(fetchTaskChartsQuery, {
    variables: { tsName, firmId, taskId },
  })
  if (loading || error || !data) {
    return null
  }
  return <DailyTimeseriesChart {...props} data={data} />
}
